import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import defaultTopBarLogo from '../assets/shared/icons/neoterra_logo.svg';
import desktopMenuLogo from '../assets/Group12.svg';
import { ourStoryHeroShot } from '../cdn/ourStoryMedia';
import headerBellIcon from '../assets/bell.svg';
import headerBellBlackIcon from '../assets/bellBlack.svg';
import headerCallIcon from '../assets/iconsax-call.svg';
import '../pages/LandingPage.css';

const desktopNavLinkClass =
  "inline-flex items-center gap-1.5 whitespace-nowrap border-0 bg-transparent px-1 py-2 font-['Cinzel',serif] text-[14px] font-normal uppercase leading-[20px] tracking-[0.7px] text-white no-underline transition-colors hover:text-[#e8d9c4] focus-visible:outline-none focus-visible:underline focus-visible:decoration-white focus-visible:underline-offset-4";

const desktopCallClass =
  "box-border inline-flex h-[34.71074295043945px] w-[82.6446304321289px] shrink-0 items-center justify-center whitespace-nowrap rounded-[4.96px] border-[0.41px] border-solid border-white bg-transparent font-['Poppins',sans-serif] text-[10px] font-normal uppercase leading-none tracking-[0.58px] text-white no-underline opacity-100 transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/90";

const desktopEnquireClass =
  "box-border inline-flex h-[33.528926849365234px] w-[115px] shrink-0 cursor-pointer items-center justify-center gap-[8.26px] whitespace-nowrap border-0 px-[18.18px] py-[8.26px] text-center font-['Poppins',sans-serif] text-[12px] font-normal uppercase leading-[16.53px] tracking-[0.58px] text-white opacity-100 transition-[filter] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/90 rounded-[4.96px] bg-[linear-gradient(180deg,#BC8639_0%,#C9A961_100%)]";

const homeHeroTextLinkClass =
  "home-hero-bar-text inline-flex items-center justify-center border-0 bg-transparent p-0 font-['Poppins',sans-serif] text-[18px] font-light uppercase leading-[18px] tracking-[0px] text-white no-underline align-middle transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/90";

const desktopMenuLinkClass = 'mobile-menu-desktop-link';
const desktopMenuSublinkClass = 'mobile-menu-desktop-link mobile-menu-desktop-link--sub';

const desktopDropdownItemClass =
  "block px-[18px] py-2.5 font-['Poppins',sans-serif] text-[12px] font-normal uppercase tracking-[0.06em] text-[#f5f5f5] no-underline hover:bg-white/[0.06] hover:text-[#bc8639] focus-visible:outline-none focus-visible:bg-white/[0.06] focus-visible:text-[#bc8639]";

const PROJECT_LINKS = {
  eira: '/projects/eira',
  elmora: '/projects/elmora',
};

/**
 * Shared top bar + full-screen mobile menu for public pages.
 * Desktop (lg:): horizontal nav + Call us + Enquire in the bar (Enquire opens the mailing list modal).
 * Hero mail lives only in each page’s hero row (mobile), same idea as the scroll/mouse control — not a fixed global button.
 * @param {(e?: React.MouseEvent) => void} [onEnquireClick] - Mailing list modal; enables Call us + Enquire on desktop and hero mail where wired.
 * @param {string} [phoneHref] - `tel:` link for desktop Call us (default matches sticky nav).
 */
const SiteMobileHeader = ({
  logoSrc = defaultTopBarLogo,
  children = null,
  barClassName = '',
  hideBar = false,
  menuOpen: menuOpenProp,
  onMenuOpenChange,
  onEnquireClick,
  phoneHref = 'tel:+971501627408',
  /** Home first screen: transparent bar, MENU | logo | Call us / Enquire */
  variant = 'default',
}) => {
  const isHomeHero = variant === 'homeHero';
  const { pathname } = useLocation();
  const useBlackBell = pathname.startsWith('/careers')
    || pathname.startsWith('/media-centre')
    || pathname.startsWith('/press-release')
    || pathname.startsWith('/news');

  const [internalOpen, setInternalOpen] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [desktopNewsOpen, setDesktopNewsOpen] = useState(false);
  const [desktopProjectsOpen, setDesktopProjectsOpen] = useState(false);
  const desktopNewsRef = useRef(null);
  const desktopProjectsRef = useRef(null);
  const controlled = menuOpenProp !== undefined;
  const isMenuOpen = controlled ? menuOpenProp : internalOpen;

  const setMenuOpenState = useCallback((next) => {
    if (controlled) {
      onMenuOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  }, [controlled, onMenuOpenChange]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('mobile-menu-open');
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      setProjectsExpanded(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!desktopNewsOpen) return undefined;
    const onDoc = (e) => {
      if (desktopNewsRef.current && !desktopNewsRef.current.contains(e.target)) {
        setDesktopNewsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [desktopNewsOpen]);

  useEffect(() => {
    if (!desktopProjectsOpen) return undefined;
    const onDoc = (e) => {
      if (desktopProjectsRef.current && !desktopProjectsRef.current.contains(e.target)) {
        setDesktopProjectsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [desktopProjectsOpen]);

  const openMenu = useCallback(() => setMenuOpenState(true), [setMenuOpenState]);
  const closeMenu = useCallback(() => setMenuOpenState(false), [setMenuOpenState]);
  const openMenuFromClick = useCallback((e) => {
    e.stopPropagation();
    openMenu();
  }, [openMenu]);
  const closeMenuFromClick = useCallback((e) => {
    e.stopPropagation();
    closeMenu();
  }, [closeMenu]);
  const closeMenuFromLink = useCallback((e) => {
    e.stopPropagation();
    closeMenu();
  }, [closeMenu]);

  const toggleProjects = useCallback((e) => {
    e.stopPropagation();
    setProjectsExpanded((v) => !v);
  }, []);

  const toggleDesktopNews = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDesktopProjectsOpen(false);
    setDesktopNewsOpen((v) => !v);
  }, []);

  const closeDesktopNews = useCallback(() => setDesktopNewsOpen(false), []);

  const toggleDesktopProjects = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDesktopNewsOpen(false);
    setDesktopProjectsOpen((v) => !v);
  }, []);

  const closeDesktopProjects = useCallback(() => setDesktopProjectsOpen(false), []);

  return (
    <>
      {!hideBar && !isMenuOpen && (
        <header
          className={
            isHomeHero
              ? `home-top-bar site-header-shell home-top-bar--hero flex flex-col items-center !bg-transparent !border-0 lg:!h-[96px] lg:!min-h-[96px] lg:flex-row lg:items-center lg:justify-center lg:!bg-transparent lg:px-[clamp(28px,4vw,72px)] lg:py-0 lg:!border-0 ${barClassName}`.trim()
              : `home-top-bar site-header-shell flex flex-col items-center lg:!h-[86px] lg:!min-h-[86px] lg:flex-row lg:items-center lg:justify-center lg:!bg-[#000000c2] lg:px-[clamp(20px,3vw,56px)] lg:py-0 lg:border-b lg:border-white/10 ${barClassName}`.trim()
          }
        >
          <div className={`mobile-hero-bar site-header-mobile-row w-full ${isHomeHero ? 'site-header-mobile-row--hero' : ''}`}>
            <button
              type="button"
              className={`mobile-menu-btn site-header-mobile-menu-btn ${isMenuOpen ? 'is-open' : ''}`}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              onClick={openMenuFromClick}
            >
              <span className="site-header-hero-menu-icon" aria-hidden="true">
                <span className="site-header-hero-menu-icon__line" />
                <span className="site-header-hero-menu-icon__middle">
                  <span className="site-header-hero-menu-icon__seg" />
                  <span className="site-header-hero-menu-icon__seg" />
                </span>
                <span className="site-header-hero-menu-icon__line" />
              </span>
            </button>
            <Link to="/" onClick={closeMenu} className="mobile-hero-logo-link site-header-mobile-logo-link" aria-label="Neoterra home">
              <img src={logoSrc} alt="Neoterra Developments Logo" className="mobile-hero-logo site-header-mobile-logo" width="50.4" height="32" fetchPriority="high" />
            </Link>
            <div className="site-header-mobile-actions lg:hidden">
              <a href={phoneHref} className="site-header-mobile-action site-header-mobile-action--call" aria-label="Call us">
                <img src={headerCallIcon} alt="" className="site-header-mobile-action__icon" width="24" height="24" />
              </a>
              {onEnquireClick ? (
                <button
                  type="button"
                  className="site-header-mobile-action site-header-mobile-action--bell"
                  aria-label="Open mailing list form"
                  onClick={onEnquireClick}
                >
                  <img
                    src={useBlackBell ? headerBellBlackIcon : headerBellIcon}
                    alt=""
                    className="site-header-mobile-action__icon site-header-mobile-action__icon--bell"
                    width="24"
                    height="24"
                  />
                </button>
              ) : null}
            </div>
          </div>

          {isHomeHero ? (
            <div
              className="site-header-desktop-grid site-header-desktop-grid--hero hidden h-full w-full max-w-[1920px] items-center lg:grid lg:h-[96px]"
              aria-label="Desktop primary navigation"
            >
              <div className="site-header-desktop-rail site-header-desktop-rail--left">
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-white"
                  aria-label="Open menu"
                  aria-expanded={isMenuOpen}
                  onClick={openMenuFromClick}
                >
                  <span className="site-header-hero-menu-icon" aria-hidden="true">
                    <span className="site-header-hero-menu-icon__line" />
                    <span className="site-header-hero-menu-icon__middle">
                      <span className="site-header-hero-menu-icon__seg" />
                      <span className="site-header-hero-menu-icon__seg" />
                    </span>
                    <span className="site-header-hero-menu-icon__line" />
                  </span>
                  <span className="home-hero-bar-text font-['Poppins',sans-serif] text-[18px] font-light uppercase leading-[18px] tracking-[0px] align-middle">
                    Menu
                  </span>
                </button>
              </div>

              <div className="flex min-w-0 items-center justify-center justify-self-center">
                <Link to="/" className="inline-flex shrink-0 items-center leading-none" onClick={closeMenu} aria-label="Neoterra home">
                  <img
                    src={logoSrc}
                    alt="Neoterra Developments Logo"
                    className="site-header-desktop-logo block h-[57px] w-[91px] object-contain lg:h-[76px] lg:w-[121px]"
                    width="121"
                    height="76"
                    fetchPriority="high"
                  />
                </Link>
              </div>

              <div className="site-header-desktop-rail site-header-desktop-rail--right site-header-desktop-rail--hero-actions">
                {onEnquireClick ? (
                  <div className="site-header-desktop-actions">
                    <a href={phoneHref} className={homeHeroTextLinkClass} aria-label="Call us">
                      Call us
                    </a>
                    <button
                      type="button"
                      className={`${homeHeroTextLinkClass} cursor-pointer`}
                      onClick={onEnquireClick}
                    >
                      Enquire
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div
              className="site-header-desktop-grid hidden h-full w-full max-w-[1440px] items-center lg:grid lg:h-[86px]"
              aria-label="Desktop primary navigation"
            >
              <div className="site-header-desktop-rail site-header-desktop-rail--left">
                <Link to="/" className="flex shrink-0 items-center leading-none" onClick={closeMenu} aria-label="Neoterra home">
                  <img
                    src={logoSrc}
                    alt=""
                    className="site-header-desktop-logo block h-[52px] w-auto max-w-[210px] object-contain"
                    width="353"
                    height="222"
                    fetchPriority="high"
                  />
                </Link>
              </div>

              <nav className="site-header-desktop-nav flex min-w-0 flex-wrap items-center justify-center gap-x-[clamp(8px,1.4vw,26px)] gap-y-1" aria-label="Main">
                <Link to="/our-story" className={`${desktopNavLinkClass} site-header-nav-link`} onClick={closeMenu}>
                  Our story
                </Link>
                <Link to="/careers" className={`${desktopNavLinkClass} site-header-nav-link`} onClick={closeMenu}>
                  Careers
                </Link>
                <div className="relative" ref={desktopNewsRef}>
                  <button
                    type="button"
                    className={`${desktopNavLinkClass} site-header-nav-link site-header-news-toggle cursor-pointer`}
                    aria-expanded={desktopNewsOpen}
                    aria-haspopup="true"
                    onClick={toggleDesktopNews}
                  >
                    <span>News &amp; media</span>
                    <ChevronDown
                      className={`shrink-0 text-white/90 transition-transform motion-reduce:transition-none ${desktopNewsOpen ? 'rotate-180' : ''}`}
                      size={16}
                      strokeWidth={1.75}
                      aria-hidden={true}
                    />
                  </button>
                  {desktopNewsOpen && (
                    <div
                      className="absolute left-1/2 top-[calc(100%+8px)] z-[200] min-w-[216px] -translate-x-1/2 rounded-lg border border-white/12 bg-[#161616] py-2 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
                      role="menu"
                    >
                      <Link to="/press-release" className={desktopDropdownItemClass} role="menuitem" onClick={closeDesktopNews}>
                        Press release
                      </Link>
                      <Link to="/media-centre" className={desktopDropdownItemClass} role="menuitem" onClick={closeDesktopNews}>
                        Media centre
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative" ref={desktopProjectsRef}>
                  <button
                    type="button"
                    className={`${desktopNavLinkClass} site-header-nav-link site-header-projects-toggle cursor-pointer`}
                    aria-expanded={desktopProjectsOpen}
                    aria-haspopup="true"
                    onClick={toggleDesktopProjects}
                  >
                    <span>Projects</span>
                    <ChevronDown
                      className={`shrink-0 text-white/90 transition-transform motion-reduce:transition-none ${desktopProjectsOpen ? 'rotate-180' : ''}`}
                      size={16}
                      strokeWidth={1.75}
                      aria-hidden={true}
                    />
                  </button>
                  {desktopProjectsOpen && (
                    <div
                      className="absolute left-1/2 top-[calc(100%+8px)] z-[200] min-w-[176px] -translate-x-1/2 rounded-lg border border-white/12 bg-[#161616] py-2 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
                      role="menu"
                    >
                      <Link
                        to={PROJECT_LINKS.eira}
                        className={desktopDropdownItemClass}
                        role="menuitem"
                        onClick={closeDesktopProjects}
                      >
                        Eira
                      </Link>
                      <Link
                        to={PROJECT_LINKS.elmora}
                        className={desktopDropdownItemClass}
                        role="menuitem"
                        onClick={closeDesktopProjects}
                      >
                        Elmora
                      </Link>
                    </div>
                  )}
                </div>
              </nav>

              <div className="site-header-desktop-rail site-header-desktop-rail--right">
                {onEnquireClick ? (
                  <div className="site-header-desktop-actions">
                    <a href={phoneHref} className={`${desktopCallClass} site-header-call-link`} aria-label="Call us">
                      Call us
                    </a>
                    <button type="button" className={`${desktopEnquireClass} site-header-enquire-button`} onClick={onEnquireClick}>
                      Enquire now
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </header>
      )}

      {children}
      <div
        className={`mobile-menu-overlay ${isMenuOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!isMenuOpen}
      >
        <div className="mobile-menu-mobile-shell lg:hidden">
          <div className="mobile-menu-overlay-top">
            <button
              type="button"
              className="mobile-menu-close-btn"
              aria-label="Close menu"
              onClick={closeMenuFromClick}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <path d="M1 1L7 7M7 1L1 7" stroke="#BC8639" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <Link
            to="/"
            className="mobile-menu-logo-button"
            aria-label="Go to home screen"
            onClick={closeMenuFromLink}
          >
            <img src={logoSrc} alt="Neoterra Developments Logo" className="mobile-menu-logo-center" width="91" height="57" />
          </Link>
          <nav className="mobile-menu-nav" aria-label="Primary">
            <Link to="/media-centre" className="mobile-menu-link" onClick={closeMenuFromLink}>
              Media
            </Link>
            <Link to="/careers" className="mobile-menu-link" onClick={closeMenuFromLink}>
              Careers
            </Link>
            <div className="mobile-menu-news-group" aria-label="Projects">
              <button
                type="button"
                className="mobile-menu-link mobile-menu-link-news-row mobile-menu-news-toggle"
                aria-expanded={projectsExpanded}
                aria-controls="projects-sublinks"
                onClick={toggleProjects}
              >
                <span className="mobile-menu-link-news">Projects</span>
                <span
                  className={`mobile-menu-news-caret${projectsExpanded ? ' mobile-menu-news-caret--expanded' : ''}`}
                  aria-hidden="true"
                >
                  ⌃
                </span>
              </button>
              <div
                id="projects-sublinks"
                className="mobile-menu-news-sublinks"
                hidden={!projectsExpanded}
              >
                <Link to={PROJECT_LINKS.eira} className="mobile-menu-sublink" onClick={closeMenuFromLink}>
                  Eira
                </Link>
                <Link to={PROJECT_LINKS.elmora} className="mobile-menu-sublink" onClick={closeMenuFromLink}>
                  Elmora
                </Link>
              </div>
            </div>
            <Link to="/our-story" className="mobile-menu-link" onClick={closeMenuFromLink}>
              Our Story
            </Link>
            <Link to="/press-release" className="mobile-menu-link" onClick={closeMenuFromLink}>
              Press Release
            </Link>
          </nav>
        </div>

        <aside className="mobile-menu-desktop-left hidden lg:flex" aria-label="Site navigation">
          <div className="mobile-menu-desktop-header">
            <button
              type="button"
              className="mobile-menu-desktop-back"
              aria-label="Close menu"
              onClick={closeMenuFromClick}
            >
              <svg
                className="mobile-menu-desktop-back-icon"
                width="43"
                height="43"
                viewBox="0 0 43 43"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M16.6 11 6 21.5 16.6 32M6 21.5H36"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="miter"
                />
              </svg>
            </button>
            <Link
              to="/"
              className="mobile-menu-desktop-logo-link"
              aria-label="Neoterra home"
              onClick={closeMenuFromLink}
            >
              <img
                src={desktopMenuLogo}
                alt="Neoterra Developments Logo"
                className="mobile-menu-desktop-logo"
                width="121"
                height="76"
              />
            </Link>
          </div>

          <div className="mobile-menu-desktop-body">
            <nav className="mobile-menu-desktop-nav" aria-label="Primary">
              <Link to="/media-centre" className={desktopMenuLinkClass} onClick={closeMenuFromLink}>
                Media
              </Link>
              <Link to="/careers" className={desktopMenuLinkClass} onClick={closeMenuFromLink}>
                Career
              </Link>
              <div className="mobile-menu-desktop-projects-group">
                <span
                  className={`${desktopMenuLinkClass} mobile-menu-desktop-projects-label`}
                  aria-controls="desktop-menu-projects-panel"
                >
                  Projects
                </span>
                <div id="desktop-menu-projects-panel" className="mobile-menu-desktop-projects-panel">
                  <div className="mobile-menu-desktop-projects-panel__inner">
                    <Link to={PROJECT_LINKS.elmora} className={desktopMenuSublinkClass} onClick={closeMenuFromLink}>
                      Elmora
                    </Link>
                    <Link to={PROJECT_LINKS.eira} className={desktopMenuSublinkClass} onClick={closeMenuFromLink}>
                      Eira
                    </Link>
                  </div>
                </div>
              </div>
              <Link to="/our-story" className={desktopMenuLinkClass} onClick={closeMenuFromLink}>
                Our Story
              </Link>
              <Link to="/press-release" className={desktopMenuLinkClass} onClick={closeMenuFromLink}>
                Press Release
              </Link>
            </nav>
            <div className="mobile-menu-desktop-divider" aria-hidden="true" />
          </div>
        </aside>

        <div className="mobile-menu-desktop-visual hidden lg:block" aria-hidden="true">
          <img
            src={ourStoryHeroShot}
            alt=""
            className="mobile-menu-desktop-visual-img"
            width={960}
            height={1080}
            decoding="async"
          />
        </div>
      </div>
    </>
  );
};

export default memo(SiteMobileHeader);
